/**
 * Zustand store для управления уведомлениями колокольчика
 * Поддерживает реал-тайм обновления через WebSocket
 */

import { create } from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'
import { 
  UserNotification, 
  NotificationSettings,
  getNotifications,
  getNotificationStats,
  markNotificationsRead,
  markAllNotificationsRead,
  deleteNotification,
  deleteAllNotifications,
  deleteNotificationsByOrder,
  getNotificationSettings,
  updateNotificationSettings
} from '../api/notificationsApi'
import { logger } from '../utils/logger'

// ============================================
// ТИПЫ СОСТОЯНИЯ
// ============================================

interface NotificationsState {
  // Данные
  notifications: UserNotification[]
  unreadCount: number
  totalCount: number
  settings: NotificationSettings | null
  
  // Состояние загрузки
  isLoading: boolean
  isLoadingStats: boolean
  isLoadingSettings: boolean
  
  // Состояние UI
  isDropdownOpen: boolean
  hasMore: boolean
  currentOffset: number
  
  // Ошибки
  error: string | null
  
  // Действия - загрузка данных
  loadNotifications: (params?: { limit?: number; offset?: number; only_unread?: boolean }) => Promise<void>
  loadMoreNotifications: () => Promise<void>
  refreshStats: () => Promise<void>
  loadSettings: () => Promise<void>
  
  // Действия - управление уведомлениями
  markAsRead: (notificationIds: string[]) => Promise<void>
  markAllAsRead: () => Promise<void>
  deleteNotificationById: (notificationId: string) => Promise<void>
  deleteAllNotifications: () => Promise<void>
  deleteNotificationsByOrder: (orderId: string) => Promise<void>
  
  // Действия - настройки
  updateSettings: (updates: Partial<NotificationSettings>) => Promise<void>
  
  // Действия - UI
  toggleDropdown: () => void
  closeDropdown: () => void
  setIsDropdownOpen: (open: boolean) => void
  
  // Действия - реал-тайм обновления
  addNotification: (notification: UserNotification) => void
  updateNotificationStatus: (notificationIds: string[], isRead: boolean) => void
  removeNotification: (notificationId: string) => void
  
  // Утилиты
  clearError: () => void
  reset: () => void
}

// ============================================
// НАЧАЛЬНОЕ СОСТОЯНИЕ
// ============================================

const initialState = {
  notifications: [],
  unreadCount: 0,
  totalCount: 0,
  settings: null,
  isLoading: false,
  isLoadingStats: false,
  isLoadingSettings: false,
  isDropdownOpen: false,
  hasMore: false,
  currentOffset: 0,
  error: null,
}

// ============================================
// STORE
// ============================================

export const useNotificationsStore = create<NotificationsState>()(
  subscribeWithSelector((set, get) => ({
    ...initialState,
    
    // ============================================
    // ЗАГРУЗКА ДАННЫХ
    // ============================================
    
    loadNotifications: async (params = {}) => {
      try {
        set({ isLoading: true, error: null })
        
        const { limit = 10, offset = 0, only_unread = false } = params
        
        const response = await getNotifications({ limit, offset, only_unread })
        
        if (response.success) {
          set({
            notifications: offset === 0 ? response.notifications : [...get().notifications, ...response.notifications],
            unreadCount: response.unread_count,
            totalCount: response.total_count,
            hasMore: response.has_more,
            currentOffset: offset + response.notifications.length,
            isLoading: false
          })
        } else {
          set({ error: 'Ошибка загрузки уведомлений', isLoading: false })
        }
      } catch (error) {
        logger.error('Ошибка загрузки уведомлений:', error)
        set({ error: 'Ошибка загрузки уведомлений', isLoading: false })
      }
    },
    
    loadMoreNotifications: async () => {
      const { hasMore, currentOffset, isLoading } = get()
      if (!hasMore || isLoading) return
      
      await get().loadNotifications({ offset: currentOffset })
    },
    
    refreshStats: async () => {
      try {
        set({ isLoadingStats: true })
        
        const response = await getNotificationStats()
        
        if (response.success) {
          set({
            unreadCount: response.unread_count,
            totalCount: response.total_count,
            isLoadingStats: false
          })
        } else {
          set({ isLoadingStats: false })
        }
      } catch (error) {
        logger.error('Ошибка загрузки статистики:', error)
        set({ isLoadingStats: false })
      }
    },
    
    loadSettings: async () => {
      try {
        set({ isLoadingSettings: true, error: null })
        
        const response = await getNotificationSettings()
        
        if (response.success) {
          set({ settings: response.settings, isLoadingSettings: false })
        } else {
          set({ error: 'Ошибка загрузки настроек', isLoadingSettings: false })
        }
      } catch (error) {
        logger.error('Ошибка загрузки настроек:', error)
        set({ error: 'Ошибка загрузки настроек', isLoadingSettings: false })
      }
    },
    
    // ============================================
    // УПРАВЛЕНИЕ УВЕДОМЛЕНИЯМИ
    // ============================================
    
    markAsRead: async (notificationIds: string[]) => {
      try {
        // Оптимистичное обновление UI
        get().updateNotificationStatus(notificationIds, true)
        
        const response = await markNotificationsRead(notificationIds)
        
        if (response.success) {
          // Обновляем счетчик
          await get().refreshStats()
          logger.log(`✅ Отмечено как прочитанные: ${response.marked_count} уведомлений`)
        } else {
          // Откатываем изменения при ошибке
          get().updateNotificationStatus(notificationIds, false)
          set({ error: 'Ошибка отметки прочитанными' })
        }
      } catch (error) {
        logger.error('Ошибка отметки прочитанными:', error)
        // Откатываем изменения при ошибке
        get().updateNotificationStatus(notificationIds, false)
        set({ error: 'Ошибка отметки прочитанными' })
      }
    },
    
    markAllAsRead: async () => {
      try {
        const response = await markAllNotificationsRead()
        
        if (response.success) {
          // Обновляем все уведомления
          set(state => ({
            notifications: state.notifications.map(notif => ({
              ...notif,
              is_read: true,
              read_at: new Date().toISOString()
            })),
            unreadCount: 0
          }))
          
          logger.log(`✅ Все уведомления отмечены как прочитанные: ${response.marked_count}`)
        } else {
          set({ error: 'Ошибка отметки всех прочитанными' })
        }
      } catch (error) {
        logger.error('Ошибка отметки всех прочитанными:', error)
        set({ error: 'Ошибка отметки всех прочитанными' })
      }
    },
    
    deleteNotificationById: async (notificationId: string) => {
      try {
        // Оптимистичное удаление из UI
        get().removeNotification(notificationId)
        
        const response = await deleteNotification(notificationId)
        
        if (response.success) {
          // Обновляем счетчики
          await get().refreshStats()
          logger.log(`🗑️ Уведомление удалено: ${notificationId}`)
        } else {
          // Восстанавливаем при ошибке (нужно перезагрузить)
          await get().loadNotifications({ offset: 0 })
          set({ error: 'Ошибка удаления уведомления' })
        }
      } catch (error) {
        logger.error('Ошибка удаления уведомления:', error)
        // Восстанавливаем при ошибке
        await get().loadNotifications({ offset: 0 })
        set({ error: 'Ошибка удаления уведомления' })
      }
    },
    
    deleteAllNotifications: async () => {
      try {
        set({ isLoading: true, error: null })
        
        const response = await deleteAllNotifications()
        
        if (response.success) {
          // Очищаем все уведомления из стейта
          set({
            notifications: [],
            unreadCount: 0,
            totalCount: 0,
            isLoading: false
          })
          logger.log(`🗑️ Удалено ${response.deleted_count} уведомлений`)
        } else {
          set({ error: 'Ошибка массового удаления уведомлений', isLoading: false })
        }
      } catch (error) {
        logger.error('Ошибка массового удаления уведомлений:', error)
        set({ error: 'Ошибка массового удаления уведомлений', isLoading: false })
      }
    },
    
    deleteNotificationsByOrder: async (orderId: string) => {
      try {
        const response = await deleteNotificationsByOrder(orderId)
        
        if (response.success) {
          // Удаляем уведомления по заказу из стейта
          const state = get()
          const filteredNotifications = state.notifications.filter(n => n.order_id !== orderId)
          const deletedCount = state.notifications.length - filteredNotifications.length
          
          set({
            notifications: filteredNotifications,
            unreadCount: Math.max(0, state.unreadCount - deletedCount),
            totalCount: Math.max(0, state.totalCount - deletedCount)
          })
          
          logger.log(`🗑️ Удалено ${response.deleted_count} уведомлений по заказу ${orderId}`)
        }
      } catch (error) {
        logger.error('Ошибка удаления уведомлений по заказу:', error)
        set({ error: 'Ошибка удаления уведомлений по заказу' })
      }
    },
    
    // ============================================
    // НАСТРОЙКИ
    // ============================================
    
    updateSettings: async (updates: Partial<NotificationSettings>) => {
      try {
        const response = await updateNotificationSettings(updates)
        
        if (response.success) {
          set({ settings: response.settings })
          logger.log('⚙️ Настройки уведомлений обновлены')
        } else {
          set({ error: 'Ошибка обновления настроек' })
        }
      } catch (error) {
        logger.error('Ошибка обновления настроек:', error)
        set({ error: 'Ошибка обновления настроек' })
      }
    },
    
    // ============================================
    // UI ДЕЙСТВИЯ
    // ============================================
    
    toggleDropdown: () => {
      set(state => ({ isDropdownOpen: !state.isDropdownOpen }))
    },
    
    closeDropdown: () => {
      set({ isDropdownOpen: false })
    },
    
    setIsDropdownOpen: (open: boolean) => {
      set({ isDropdownOpen: open })
    },
    
    // ============================================
    // РЕАЛ-ТАЙМ ОБНОВЛЕНИЯ
    // ============================================
    
    addNotification: (notification: UserNotification) => {
      // 🔥 ОТЛАДКА: Логируем добавление уведомления
      console.log('🔥 ADD NOTIFICATION: Добавляем уведомление в колокольчик!', {
        notification,
        pathname: window.location.pathname,
        isTelegram: !!(window as any)?.Telegram?.WebApp
      })
      
      set(state => ({
        notifications: [notification, ...state.notifications],
        unreadCount: state.unreadCount + (notification.is_read ? 0 : 1),
        totalCount: state.totalCount + 1
      }))
      
      logger.log('🔔 Новое уведомление добавлено:', notification.title)
    },
    
    updateNotificationStatus: (notificationIds: string[], isRead: boolean) => {
      set(state => {
        const updatedNotifications = state.notifications.map(notif => {
          if (notificationIds.includes(notif.id)) {
            return {
              ...notif,
              is_read: isRead,
              read_at: isRead ? new Date().toISOString() : undefined
            }
          }
          return notif
        })
        
        // Пересчитываем количество непрочитанных
        const unreadCount = updatedNotifications.filter(n => !n.is_read).length
        
        return {
          notifications: updatedNotifications,
          unreadCount
        }
      })
    },
    
    removeNotification: (notificationId: string) => {
      set(state => {
        const notificationToRemove = state.notifications.find(n => n.id === notificationId)
        const filteredNotifications = state.notifications.filter(n => n.id !== notificationId)
        
        return {
          notifications: filteredNotifications,
          unreadCount: notificationToRemove && !notificationToRemove.is_read 
            ? state.unreadCount - 1 
            : state.unreadCount,
          totalCount: state.totalCount - 1
        }
      })
    },
    
    // ============================================
    // УТИЛИТЫ
    // ============================================
    
    clearError: () => {
      set({ error: null })
    },
    
    reset: () => {
      set(initialState)
    }
  }))
)

// ============================================
// СЕЛЕКТОРЫ
// ============================================

/**
 * Хук для получения только непрочитанных уведомлений
 */
export const useUnreadNotifications = () => {
  return useNotificationsStore(state => 
    state.notifications.filter(notification => !notification.is_read)
  )
}

/**
 * Хук для получения уведомлений по типу
 */
export const useNotificationsByType = (type: UserNotification['type']) => {
  return useNotificationsStore(state => 
    state.notifications.filter(notification => notification.type === type)
  )
}

/**
 * Хук для получения счетчика непрочитанных
 */
export const useUnreadCount = () => {
  return useNotificationsStore(state => state.unreadCount)
}

/**
 * Хук для получения состояния загрузки
 */
export const useNotificationsLoading = () => {
  return useNotificationsStore(state => ({
    isLoading: state.isLoading,
    isLoadingStats: state.isLoadingStats,
    isLoadingSettings: state.isLoadingSettings
  }))
}
